/**
 * individualConfigPrewarm.ts — variant1 の config.individual(secondaryApp)を「route lazy 開始と同じ tick」で
 *   並列 prewarm する共通 helper(Codex chunkgating 監査 MAJOR-1/2 是正・2026-09-27)。
 *
 * 背景(MAJOR-1): chunk gating で config.individual を login chunk から析出した結果、
 *   `boot()`(index.tsx)の prewarm は「初回 boot 時の pathname / Launcher dispatch」でしか発火せん。
 *   非個人 route で boot → React Router の SPA 内遷移で /i/... や /c/{tenant}/{customer} へ入ると
 *   `boot()` は再実行されず、IndividualLoginPage chunk 取得 → mount → effect で config.individual を
 *   初めて取得=直列 chunk RTT が variant1 の token_lookup 前に挿入される(login 不可侵違反)。
 *   → 是正: React.lazy factory(App.tsx)から本 helper を「page import と同 tick」で呼び、
 *     variant1 と確定できる時だけ config.individual を並列取得する。page import は variant 解決を待たせない。
 *
 * 背景(MAJOR-2): prewarm 可否を **注入可能な純関数**に分離し、variant2 で importer 呼出0を
 *   behavior test(individualConfigPrewarm.test.ts)で固定する。テキスト番人だけでは
 *   「本番 host 分岐内の import を guard 外へ移動」する敵対変異を赤にできんかった(Codex 実証)。
 *
 * ★安全掟:
 *   - Firebase を static import しない(この module は authVariantState[Firebase-free]だけを static 依存)。
 *     config.individual は importFn 内の dynamic import のみ=main/個人 chunk のグラフに secondaryApp を載せない。
 *   - 本番 host(isDefinitelyProductionHost)= variant 必ず variant1(server 二重ロック)ゆえ即 prewarm(latency 不可侵)。
 *   - 非本番(preview/localhost)= server 判定後 variant1 の時だけ prewarm。variant2 は importer を呼ばない(構築させない)。
 *   - 全 error/timeout = variant1 fail-safe(authVariantResolved の契約)ゆえ prewarm する(安全側=既存 Firebase 経路)。
 *   - best-effort(prewarm 失敗で login は止めない)。
 */
import { isDefinitelyProductionHost, authVariantResolved } from './authVariantState';

/**
 * variant1 と確定できる時だけ config.individual を並列 prewarm する。
 * @param importIndividualConfig config.individual を取得する関数(既定=dynamic import・テストで spy 注入可能)。
 */
export function prewarmIndividualConfig(
  importIndividualConfig: () => Promise<unknown> = () => import('../firebase/config.individual'),
): void {
  runPrewarm(importIndividualConfig);
}

/**
 * IndividualLoginPage 用の React.lazy loader(Codex 再監査 MAJOR-1 是正・2026-09-27)。
 *   page import を先に開始し、**同一同期 turn** で config prewarm を発火して、page import の Promise を
 *   **そのまま**返す(prewarm を await しない=variant 解決で login 描画を1msも待たせない)。
 *
 * ★注入可能な実行関数に分離した理由(Codex 再監査 MAJOR-1): App.tsx の lazy factory 内に直書きすると
 *   「App 側の import+呼出を削除(コメント残し)」する敵対変異をテキスト番人が赤にできんかった。
 *   本 loader の契約(両方呼ぶ/順序/pass-through/await せん)は behavior test
 *   (individualConfigPrewarm.test.ts)で固定し、App が本 loader を実際に使うことは
 *   variant2ChunkGatingIntegrity #4 が**コメント除去済み source の構文単位**で凍結する。
 */
export function loadIndividualPageWithConfigPrewarm<T>(
  importPage: () => Promise<T>,
  prewarm: () => void = prewarmIndividualConfig,
): Promise<T> {
  // page import を先に開始(chunk fetch を最速で走らせる=従来 factory と同順)
  const pageImport = importPage();
  // 同 tick で prewarm(fire-and-forget)。prewarm の失敗で page load を止めない(best-effort)。
  try {
    prewarm();
  } catch {
    /* best-effort */
  }
  // ★page import の Promise をそのまま返す(prewarm を await しない=render barrier ゼロ)
  return pageImport;
}

function runPrewarm(importIndividualConfig: () => Promise<unknown>): void {
  try {
    if (isDefinitelyProductionHost(window.location.hostname)) {
      // 本番 = variant 必ず variant1 → 即 prewarm(従来 chunk 評価と同時期・latency 不可侵)
      importIndividualConfig().catch(() => {});
      return;
    }
    // 非本番 = server 判定を待って variant1 の時だけ。variant2 は importer を呼ばない(構築させない)。
    //   fail-safe(error/timeout)= authVariantResolved が variant1 を返す=prewarm する(安全側)。
    void authVariantResolved()
      .then((v) => {
        if (v === 'variant1') importIndividualConfig().catch(() => {});
      })
      .catch(() => {});
  } catch {
    /* best-effort(window 不在等・prewarm 失敗で login は止めない) */
  }
}
